// Updated src/pages/shop/components/NavMenu/index.js - Desktop Only Version with Guest Support

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Settings,
  User,
  LayoutDashboard,
  LogOut,
  Bell,
  Home,
  MessageCircle,
  Store,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../..//../contexts/AuthContext';
import { auth, db } from '../../../../firebase/config';
import { DEFAULT_THEME } from '../../../../theme/theme';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

/* Thin vertical strip always flush with the right wall */
const NavToggleStrip = styled.button`
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 54px;
  border-radius: 8px 0 0 8px;
  background: ${props => `${props.theme?.colors?.background || '#000000'}CC`};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#800000'}30`};
  border-right: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 101;
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${props => `${props.theme?.colors?.accent || '#800000'}25`};
    color: ${props => props.theme?.colors?.accent || '#800000'};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

/* Panel that slides in from the right when open */
const NavPanel = styled.div`
  position: fixed;
  right: 18px;
  top: 50%;
  transform: translateY(-50%) translateX(${props => props.$isOpen ? '0' : 'calc(100% + 1.5rem)'});
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  height: auto;
  width: auto;
  background: ${props => `${props.theme?.colors?.background || '#000000'}CC`};
  backdrop-filter: blur(10px);
  padding: clamp(0.5rem, 0.8vh, 0.75rem);
  border-radius: clamp(8px, 1vh, 10px);
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#800000'}30`};
  display: flex;
  flex-direction: column;
  z-index: 100;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.25rem, 0.5vh, 0.4rem);
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: clamp(0.4rem, 0.65vh, 0.6rem);
  background: ${props => props.active ?
    `${props.theme?.colors?.accent || '#800000'}15` :
    'transparent'
  };
  border: none;
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  cursor: pointer;
  width: 100%;
  position: relative;
  transition: all 0.3s ease;
  border-radius: clamp(5px, 0.7vh, 7px);

  &:hover {
    background: ${props => `${props.theme?.colors?.accent || '#800000'}10`};
    transform: translateX(-2px);
  }

  .icon-container {
    margin-right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: clamp(16px, 2vh, 20px);
  }

  .label {
    opacity: 1;
    overflow: hidden;
    transition: all 0.3s ease;
    margin-left: 0.5rem;
    white-space: nowrap;
    font-size: clamp(0.7rem, 1.1vh, 0.82rem);
    font-weight: 500;
  }

  .notification-badge {
    position: absolute;
    top: 0.4rem;
    left: 1.2rem;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: ${props => props.theme?.colors?.accent || '#800000'};
    display: ${props => props.hasNotification ? 'block' : 'none'};
    border: 2px solid ${props => props.theme?.colors?.background || '#000000'};
  }
`;

const LogoutSection = styled.div`
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${props => `${props.theme?.colors?.accent || '#800000'}20`};
`;

const NavMenu = ({ theme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();

  const [isGuestUser, setIsGuestUser] = useState(false);

  const defaultTheme = {
    colors: {
      background: '#0B0B3B',
      text: '#FFFFFF',
      accent: '#800000'
    }
  };

  const menuTheme = theme || defaultTheme;

  useEffect(() => {
    const checkGuestStatus = () => {
      const currentUser = auth.currentUser;
      const guestSession = localStorage.getItem('guest_session');

      if (currentUser?.isAnonymous || guestSession) {
        setIsGuestUser(true);
      } else {
        setIsGuestUser(false);
      }
    };

    checkGuestStatus();
    const unsubscribe = auth.onAuthStateChanged(checkGuestStatus);
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  useEffect(() => {
    if (!auth.currentUser || isGuestUser) return;

    const userId = auth.currentUser.uid;
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('participants', 'array-contains', userId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let hasUnread = false;
      snapshot.docs.forEach(doc => {
        const chat = doc.data();
        if (chat.unreadCount && chat.unreadCount[userId] && chat.unreadCount[userId] > 0) {
          hasUnread = true;
        }
      });
      setUnreadMessages(hasUnread);
    });

    return () => unsubscribe();
  }, [isGuestUser]);

  const menuItems = [
    {
      icon: <Home size={20} />,
      label: 'Home',
      path: '/',
      showForGuest: true
    },
    {
      icon: <Store size={20} />,
      label: 'My Shop',
      path: user?.uid ? `/shop/${user.uid}` : '/shop/create/template',
      showForGuest: false
    },
    {
      icon: <MessageCircle size={20} />,
      label: 'Messages',
      path: '/messages',
      hasNotification: unreadMessages,
      showForGuest: true
    },
    {
      icon: <Bell size={20} />,
      label: 'Notifications',
      path: '/notifications',
      showForGuest: false
    },
    {
      icon: <User size={20} />,
      label: 'Profile',
      path: '/profile',
      showForGuest: false
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const visibleItems = menuItems.filter(item => {
    if (isGuestUser) return item.showForGuest === true;
    return true;
  });

  return (
    <>
      <NavToggleStrip
        theme={menuTheme}
        onClick={() => setIsOpen(prev => !prev)}
        title={isOpen ? 'Collapse menu' : 'Expand menu'}
      >
        {isOpen
          ? <ChevronRight size={12} />
          : <ChevronLeft size={12} />
        }
      </NavToggleStrip>

      <NavPanel theme={menuTheme} $isOpen={isOpen}>
        <NavButtonsContainer>
          {visibleItems.map((item) => (
            <NavButton
              key={item.path}
              theme={menuTheme}
              active={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
              onClick={() => handleNavigation(item.path)}
              hasNotification={item.hasNotification}
              title={item.label}
            >
              <div className="icon-container">
                {item.icon}
              </div>
              <span className="label">{item.label}</span>
              {item.hasNotification && <div className="notification-badge"></div>}
            </NavButton>
          ))}
        </NavButtonsContainer>

        <LogoutSection theme={menuTheme}>
          <NavButton theme={menuTheme} onClick={handleLogout} title="Logout">
            <div className="icon-container">
              <LogOut size={20} />
            </div>
            <span className="label">Logout</span>
          </NavButton>
        </LogoutSection>
      </NavPanel>
    </>
  );
};

export default NavMenu;
